/**
 * Step5AIAnalysis — Phase 27 (was "Step 6 — Review" pre-refactor).
 *
 * Final review: show 4 grouped cards (Property / Income / Expenses /
 * Financing) summarising every captured value, then the user hits
 * "Analyze deal" on the orchestrator footer to fire the analysis.
 *
 * NOTE: the actual AI verdict / deal-score render happens on the
 * post-submit /deals/{id} screen — this Step5 only shows the pre-submit
 * recap. The brief named it "Step5AIAnalysis" because submitting kicks off
 * the AI analysis pipeline.
 *
 * Verbatim port from DealWizard.jsx renderStep step === 6 branch.
 * Preserve as-is: items[]: ["k", v] tuples → dt/dd render; never restructure
 * because at least two e2e tests rely on the dl > div > dt+dd structure.
 */
import { propertyTypeLabel } from "@/lib/format";
import AINudge from "@/components/wizard/AINudge";
import { formatPsfPsm } from "@/lib/priceNormalisation";
import { RENTAL_STRATEGIES } from "./_shared";

export default function Step5AIAnalysis({ wizard }) {
  const { form, fmt, dealType } = wizard;
  const sqft = Number(form.property.square_feet) || 0;
  const ask = Number(form.property.asking_price) || 0;
  const arv = Number(form.analysis.arv) || 0;
  const askPsfStr = ask > 0 && sqft >= 50 ? formatPsfPsm(ask, sqft) : null;
  const arvPsfStr = arv > 0 && sqft >= 50 ? formatPsfPsm(arv, sqft) : null;
  const isFlipLike = dealType === "flip" || dealType === "brrrr";
  const propertyItems = [
    ["Country", form.property.country],
    ["Address", form.property.address],
    ["City / State / Zip", [form.property.city, form.property.state, form.property.zip].filter(Boolean).join(", ")],
    ["Type", propertyTypeLabel(form.property.property_type)],
    ["Units · Beds · Baths · Sqft", `${form.property.units} · ${form.property.bedrooms} · ${form.property.bathrooms} · ${form.property.square_feet}`],
    ["Asking price", fmt.money(form.property.asking_price)],
  ];
  if (askPsfStr) {
    propertyItems.push(["Asking $/sqft · $/sqm", askPsfStr]);
  }
  if (isFlipLike && arv > 0) {
    propertyItems.push(["ARV", fmt.money(arv)]);
    if (arvPsfStr) {
      propertyItems.push(["ARV $/sqft · $/sqm", arvPsfStr]);
    }
  }
  propertyItems.push(["Rental strategy", RENTAL_STRATEGIES.find((o) => o.id === (form.analysis.rental_strategy || "long_term"))?.label]);
  const groups = [
    {
      title: "Property",
      items: propertyItems,
    },
    {
      title: "Income",
      items: [["Monthly rent", `${fmt.money(form.analysis.monthly_rent)} / mo`]],
    },
    {
      title: "Expenses",
      items: [
        ["Taxes", `${fmt.money(form.analysis.taxes_annual)} / yr`],
        ["Insurance", `${fmt.money(form.analysis.insurance_annual)} / yr`],
        ["HOA", `${fmt.money(form.analysis.hoa_monthly)} / mo`],
        ["Utilities", `${fmt.money(form.analysis.utilities_monthly)} / mo`],
        ["Vacancy", `${form.analysis.vacancy_percent}%`],
        ["Management", `${form.analysis.management_percent}%`],
        ["Upfront repairs", fmt.money(form.analysis.repairs)],
      ],
    },
    {
      title: "Financing",
      items: form.analysis.is_cash_purchase
        ? [
            ["Cash purchase", "Yes — no loan"],
            ["Closing costs", fmt.money(form.analysis.closing_costs)],
            ["Transfer / stamp tax", fmt.money(form.analysis.transfer_tax)],
          ]
        : [
            ["Down payment", `${form.analysis.down_payment_percent}%`],
            ["Interest rate", `${form.analysis.interest_rate}%`],
            ["Loan term", `${form.analysis.loan_term_years} yrs`],
            ["Closing costs", fmt.money(form.analysis.closing_costs)],
            ["Transfer / stamp tax", fmt.money(form.analysis.transfer_tax)],
          ],
    },
  ];
  return (
    <div className="space-y-6" data-testid="review-step">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Review & analyze</h2>
        <p className="mt-1 text-sm text-slate-500">
          We’ll compute cash flow, cap rate, CoC, DSCR, and a deal score in real time.
        </p>
      </div>
      {/* Phase 36 Batch AA.3 — Proactive AI-breakdown nudge. Renders only
          for flip / BRRRR deals when projected margin clears the +65%
          "winner" bar (indigo) or drops below the 10% tight-margin bar
          (amber). Hidden entirely otherwise + for rentals / multifamily.
          The CTA reuses `wizard.submit` so there's exactly one analysis
          code-path. */}
      <AINudge wizard={wizard} />
      <div className="grid gap-4 sm:grid-cols-2">
        {groups.map((g) => (
          <div key={g.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{g.title}</h3>
            <dl className="mt-3 space-y-1.5 text-sm">
              {g.items.map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-medium text-slate-900">{v || "—"}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}
